import { useState, Fragment } from "react";

/* eslint-disable complexity */
import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import Button from "@/components/wpl/button/src";
import Image from "@/components/wpl/image/src";
import { AisSitesType } from "@/libraries/bxp-web-stargate-client/src/index";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const confluenceHeroSectionStyle = css({
    display: "flex",
    maxWidth: "1200px",
    margin: `${token("space.0")} auto ${token("space.0")} auto`,

    [media.below.sm]: {
        flexDirection: "column"
    },

    [media.above.sm]: {
        flexDirection: "row"
    }
});

const heroSectionTextStyle = css({
    "display": "flex",
    "flexDirection": "column",
    "marginBlockStart": "50px",
    "padding": `${token("space.400")} ${token("space.200")} ${token("space.0")}
        ${token("space.200")}`,

    "h1": {
        color: "#253858",
        letterSpacing: `${token("space.0")}`,
        fontWeight: "500",
        margin: `${token("space.0")} ${token("space.0")} 14px ${token(
            "space.0"
        )}`
    },

    "p": {
        color: "#091e42",
        margin: `${token("space.0")} ${token("space.0")} ${token("space.300")}
            ${token("space.0")}`,
        fontSize: "18px"
    },

    ".confluence-hero-section-cta": {
        margin: `${token("space.0")} ${token("space.0")} ${token("space.300")}
        ${token("space.0")}`,
        fontWeight: "500"
    },

    [media.below.sm]: {
        flex: "1",
        textAlign: "center",
        alignItems: "center",

        paddingBlockStart: "O",

        h1: {
            fontSize: `${token("space.400")}`,
            lineHeight: "1.25"
        }
    },

    [media.above.sm]: {
        flex: "0 0 41.66666%",
        textAlign: "left",
        alignItems: "flex-start",

        h1: {
            fontSize: "44px",
            lineHeight: "1.18181818"
        }
    },

    [media.above.lg]: {
        h1: {
            fontSize: `${token("space.600")}`,
            lineHeight: "3.5rem",
            marginBlockEnd: `${token("space.300")}`
        }
    }
});

const heroSectionImageStyle = css({
    [media.below.sm]: {
        display: "none"
    },

    [media.above.sm]: {
        flex: "0 0 50%",
        padding: `${token("space.400")} ${token("space.200")}
            ${token("space.200")} ${token("space.200")}`,
        marginInlineStart: "30px",

        img: {
            paddingBlockStart: `${token("space.250")}`,
            width: "100%"
        }
    },

    [media.above.md]: {
        position: "relative"
    },

    [media.above.lg]: {
        marginInlineEnd: "0",

        img: {
            maxWidth: "100%"
        }
    }
});

const videoCardWrapperStyles = css({
    marginInlineStart: "auto",
    marginInlineEnd: "auto",
    display: "flex",
    justifyContent: "center",

    [`@media (min-width: 540px)`]: {
        width: "66.66666%"
    },

    [media.below.sm]: {
        padding: `${token("space.200")}`
    },

    [media.above.sm]: {
        width: "83.33333%",
        padding: `${token("space.400")} ${token("space.200")}
            ${token("space.200")} ${token("space.200")}`
    },

    [media.above.md]: {
        width: "66.66666%"
    }
});

const videoCardContainerStyles = css({
    display: "flex",
    boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#ffffff",
    borderRadius: "3px",
    maxWidth: "767.99px",

    [media.below.sm]: {
        flexDirection: "column"
    },

    [media.above.sm]: {
        flexDirection: "row"
    }
});

const videoCardImageStyles = css({
    "position": "relative",
    "padding": `0 ${token("space.200")}`,
    "overflow": "hidden",

    [media.only.sm]: {
        flex: "5"
    },

    [media.above.md]: {
        flex: "5",
        height: "184px",
        paddingInlineStart: "28px",
        paddingInlineEnd: "28px"
    },

    ":hover::before": {
        backgroundColor: "#ffffff",
        transform: "scale(1.06)"
    },

    "::before": {
        position: "absolute",
        content: "",
        insetBlockStart: "0",
        insetInlineStart: "0",
        width: "100%",
        height: "100%",
        margin: "0",
        backgroundPosition: "center center",
        backgroundSize: "cover",
        backgroundColor: "rgba(23, 43, 77, 0.15)",
        backgroundBlendMode: "multiply",
        transformOrigin: "center center",
        transform: "scale(1)",
        transition: "all 1.3s",
        backgroundImage: `url(https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:83995a77-0155-4484-b349-39c9501f3313/Confluence%20PT%20thumbnail@2x.png?cdnVersion=1263)`,
        zIndex: "1",
        borderStartStartRadius: "3px",
        borderStartEndRadius: "3px",

        [media.above.sm]: {
            borderEndStartRadius: "3px"
        }
    },

    "img": {
        maxHeight: "340px",
        opacity: "0",
        width: "100%"
    },

    "svg": {
        zIndex: "2",
        position: "absolute",
        insetBlockStart: "0",
        insetInlineEnd: "0",
        insetInlineStart: "0",
        insetBlockEnd: "0",
        margin: "auto",
        fill: "rgba(255, 255, 255, 0.9)"
    }
});

const videoCardTextStyles = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "stretch",

    h3: {
        color: "#253858",
        marginBlockStart: "0",
        letterSpacing: "0.3px",
        marginBlockEnd: "14px",
        fontWeight: "500"
    },

    [media.below.sm]: {
        padding: `${token("space.400")}`,

        h3: {
            fontSize: `${token("space.250")}`,
            lineHeight: "1.2"
        }
    },

    [media.only.sm]: {
        flex: "7",
        padding: `${token("space.400")}`,

        h3: {
            fontSize: `${token("space.300")}`,
            lineHeight: "1.2"
        }
    },

    [media.above.md]: {
        flex: "7",
        padding: `${token("space.500")}`,

        h3: {
            fontSize: `${token("space.300")}`,
            lineHeight: `${token("space.400")}`
        }
    },

    a: {
        display: "flex",
        alignItems: "center",
        color: "#ffffff",

        span: {
            fontSize: `${token("space.200")}`,
            color: "#0052cc"
        },

        svg: {
            fill: "#0052cc",
            marginInlineEnd: `${token("space.100")}`
        }
    }
});

interface HeroSectionProps {
    confluenceSites: AisSitesType[];
    userProfileName: string | undefined;
}

export const HeroSection = ({
    confluenceSites,
    userProfileName
}: HeroSectionProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const intl = useIntl();
    const getItFreeText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.heroCTA",
        defaultMessage: "Get it free"
    });

    const videoCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoEmbedInline",
        "data-event": "clicked",
        "data-label": "watch the video_image"
    };

    const videoCardTextDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoLink",
        "data-event": "clicked",
        "data-label-english": "Watch the video",
        "data-label": "Watch the video"
    };

    // these were left in to signal when the variant is shown, and because
    // it is behind an exposure event this will never be shown on prod
    console.log("userProfileName", userProfileName);
    console.log("First name: ", userProfileName?.split(" "));
    console.log("most recent site", confluenceSites[0]);

    return (
        <Fragment>
            <section>
                <div css={confluenceHeroSectionStyle}>
                    <div css={heroSectionTextStyle}>
                        <h1>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.heroHeader"
                                defaultMessage="Accomplish more together"
                            />
                        </h1>
                        <p>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.heroBody"
                                defaultMessage="Confluence is your remote-friendly team workspace where knowledge and collaboration meet."
                            />
                        </p>
                        <Button
                            text={getItFreeText}
                            aria-label={getItFreeText}
                            className="confluence-hero-section-cta"
                            color="yellow"
                            url={useLocalizedHref("/software/confluence/free")}
                            dataEventProps={{
                                "data-product-key": "confluence",
                                "data-event-component": "linkButton",
                                "data-event-container": "heroSection",
                                "data-event": "clicked",
                                "data-label": getItFreeText
                            }}
                        />
                    </div>
                    <div css={heroSectionImageStyle}>
                        <Image
                            alt={intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.heroImageAltText",
                                defaultMessage:
                                    "Illustration demonstrating workflows in Confluence"
                            })}
                            src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Hero.webp"
                        />
                    </div>
                </div>
                <div
                    css={css({
                        position: "relative"
                    })}
                >
                    <div css={videoCardWrapperStyles}>
                        <div css={videoCardContainerStyles}>
                            <a
                                css={videoCardImageStyles}
                                href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                                data-schema-version="1"
                                {...videoCardDataEventProps}
                                onClick={(event) => {
                                    event.preventDefault();
                                    setIsModalOpen(true);
                                }}
                            >
                                <img
                                    src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Thumbnail.webp"
                                    alt={intl.formatMessage({
                                        id: "ConfluenceSoftwarePageTemplate.Hero.VideoThumbnailAltText",
                                        defaultMessage: "video thumbnail"
                                    })}
                                />
                                <Play size="70" />
                            </a>
                            <div css={videoCardTextStyles}>
                                <h3>
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.Hero.VideoHeader"
                                        defaultMessage={
                                            "See team collaboration come to life"
                                        }
                                    />
                                </h3>
                                <a
                                    css={css({
                                        ":hover": {
                                            svg: {
                                                color: "#0065ff",
                                                fill: "#0065ff"
                                            },
                                            span: {
                                                color: "#0065ff",
                                                fill: "#0065ff"
                                            }
                                        }
                                    })}
                                    href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                                    {...videoCardTextDataEventProps}
                                    data-schema-version="1"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setIsModalOpen(true);
                                    }}
                                >
                                    <Play size="22" />
                                    <span>
                                        <FormattedMessage
                                            id="ConfluenceSoftwarePageTemplate.Hero.PlayVideo"
                                            defaultMessage={"Watch the video"}
                                        />
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div
                        css={css({
                            position: "absolute",
                            width: "100%",
                            backgroundColor: "#deebff",
                            zIndex: "-1",
                            insetBlockEnd: "0",
                            height: "75%"
                        })}
                    />
                </div>
            </section>
            <YoutubeModal
                isOpen={isModalOpen}
                src="https://www.youtube.com/embed/jqg8o-ataIQ?autoplay=1&rel=0&origin=https%3A%2F%2Fwww.atlassian.com&widget_referrer=https%3A%2F%2Fwww.atlassian.com&enablejsapi=1&widgetid=1"
                title={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Hero.ModalYouTubeIframeTitle",
                    defaultMessage:
                        "Say hello to Confluence! | Confluence Overview | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Hero.CloseModalButtonMobileLabel",
                    defaultMessage: "Close modal"
                })}
                closeModal={() => setIsModalOpen(false)}
            />
        </Fragment>
    );
};

export default HeroSection;
